import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { http, formatApiError } from "../resources/resourceUtils";
import { FileField } from "./FileField";

const EMPTY = { title: "", description: "", category_id: "", type: "pdf", file: null, url: "", thumb: null,
    thumbnail_alt: "", tags: "", status: "draft", featured: false };

const fromResource = (r) => ({
    title: r.title, description: r.description || "", category_id: r.category_id || "", type: r.type,
    file: r.file, url: r.url || "", thumb: r.thumbnail_file_id ? { id: r.thumbnail_file_id, filename: "Current thumbnail" } : null,
    thumbnail_alt: r.thumbnail_alt || "", tags: (r.tags || []).join(", "), status: r.status, featured: !!r.featured,
});

const Label = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="block text-[15px] mb-2" style={{ color: "#4A4A30" }}>{children}</label>
);
const inputCls = "w-full rounded-xl border px-4 py-3 text-[16px] bg-white focus:outline-none focus:border-[#8A9470]";

const Picker = ({ id, value, onChange, options, testid }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="rounded-xl h-12 text-[16px]" data-testid={testid}><SelectValue placeholder="Choose" /></SelectTrigger>
        <SelectContent>
            {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>)}
        </SelectContent>
    </Select>
);

const TYPES = [{ value: "pdf", label: "PDF" }, { value: "video", label: "Video (YouTube or Vimeo)" },
    { value: "audio", label: "Audio" }, { value: "link", label: "External link" }];

export const ResourceFormDialog = ({ open, resource, categories, onClose, onSaved }) => {
    const [f, setF] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

    useEffect(() => {
        if (open) setF(resource ? fromResource(resource) : { ...EMPTY, category_id: categories[0]?.id || "" });
    }, [open, resource, categories]);

    const needsFile = f.type === "pdf" || f.type === "audio";
    const isDraft = f.status === "draft";
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const body = {
            title: f.title, description: f.description, category_id: f.category_id || null, type: f.type,
            file_id: needsFile ? f.file?.id || null : null, url: f.url || null,
            thumbnail_file_id: f.thumb?.id || null, thumbnail_alt: f.thumbnail_alt,
            tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), status: f.status, featured: f.featured,
        };
        try {
            if (resource) await http.put(`/admin/resources/${resource.id}`, body);
            else await http.post("/admin/resources", body);
            toast.success(resource ? "Resource updated" : "Resource added");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="ap-res max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="resource-form-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-[30px] font-normal" style={{ color: "#4A4A30" }}>{resource ? "Edit resource" : "Add a resource"}</DialogTitle>
                    <DialogDescription className="text-[15px]">Changes appear on the Resources page as soon as you save. Drafts can be saved without a file or link.</DialogDescription>
                </DialogHeader>
                <form onSubmit={save} className="space-y-6 mt-2">
                    <div>
                        <Label htmlFor="rf-title">Title</Label>
                        <input id="rf-title" className={inputCls} style={{ borderColor: "#E8E4DA" }} value={f.title} onChange={set("title")} required maxLength={160} data-testid="resource-form-title" />
                    </div>
                    <div>
                        <Label htmlFor="rf-desc">Short description <span style={{ color: "#7D7D68" }}>({f.description.length}/200)</span></Label>
                        <textarea id="rf-desc" rows={3} className={inputCls} style={{ borderColor: "#E8E4DA" }} value={f.description} onChange={set("description")} maxLength={200} data-testid="resource-form-description" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div><Label htmlFor="rf-cat">Category</Label>
                            <Picker id="rf-cat" value={f.category_id} onChange={set("category_id")} options={categories.map((c) => ({ value: c.id, label: c.name }))} testid="resource-form-category" /></div>
                        <div><Label htmlFor="rf-type">Type</Label>
                            <Picker id="rf-type" value={f.type} onChange={set("type")} options={TYPES} testid="resource-form-type" /></div>
                    </div>
                    {needsFile && (
                        <FileField kind={f.type} value={f.file} onChange={set("file")} label={f.type === "pdf" ? "PDF file (up to 60 MB)" : "Audio file, such as an MP3 (up to 60 MB)"} testid="resource-form-file" />
                    )}
                    {(!needsFile || (f.type === "audio" && !f.file)) && (
                        <div>
                            <Label htmlFor="rf-url">{f.type === "video" ? "YouTube or Vimeo link" : f.type === "audio" ? "Or an audio link (MP3 link or SoundCloud)" : "Link"}</Label>
                            <input id="rf-url" type="url" className={inputCls} style={{ borderColor: "#E8E4DA" }} value={f.url} onChange={set("url")} placeholder="https://" required={!needsFile && !isDraft} data-testid="resource-form-url" />
                        </div>
                    )}
                    <FileField kind="image" value={f.thumb} onChange={set("thumb")} label="Thumbnail image (optional)" testid="resource-form-thumb" />
                    {f.thumb && (
                        <div>
                            <Label htmlFor="rf-alt">Thumbnail description for screen readers</Label>
                            <input id="rf-alt" className={inputCls} style={{ borderColor: "#E8E4DA" }} value={f.thumbnail_alt} onChange={set("thumbnail_alt")} maxLength={200} data-testid="resource-form-thumb-alt" />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="rf-tags">Tags (optional, separated by commas)</Label>
                        <input id="rf-tags" className={inputCls} style={{ borderColor: "#E8E4DA" }} value={f.tags} onChange={set("tags")} data-testid="resource-form-tags" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5 items-end">
                        <div><Label htmlFor="rf-status">Status</Label>
                            <Picker id="rf-status" value={f.status} onChange={set("status")} options={[{ value: "draft", label: "Draft (hidden)" }, { value: "published", label: "Published" }]} testid="resource-form-status" /></div>
                        <label className="flex items-center gap-3 h-12 text-[15px]" style={{ color: "#4A4A30" }}>
                            <Switch checked={f.featured} onCheckedChange={set("featured")} data-testid="resource-form-featured" /> Featured (up to 3)
                        </label>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-[15px] border" style={{ borderColor: "#E8E4DA" }} data-testid="resource-form-cancel">Cancel</button>
                        <button type="submit" disabled={saving || (!isDraft && (needsFile ? !f.file && !(f.type === "audio" && f.url) : !f.url.trim()))} className="res-btn disabled:opacity-60" data-testid="resource-form-save">
                            {saving ? "Saving..." : "Save resource"}
                        </button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
